<script lang="ts">
import { defineComponent, ref, computed, watch, type PropType, onMounted } from 'vue'
import type { InputSelectOption } from '@/types/ui/inspection/common/inspectionSelectOptions'
import PhotoDBOperator from '@/accessors/indexedDB/photoDBOperator'
import LabeledCard from '@/components/inspection/common/LabeledCard.vue'
import { OVERALL_RESULT_LABEL } from '@/constants/ui/inspection/result/overallResultLabel'
import { IMAGE_COUNT_LIMIT_FOR_INSPECTION_RESULT } from '@/constants/accessor/api/common/imageCountLimit'
import { type OverallResult } from '@/types/accessor/api/emergencyInspectionResult/emergencyAssessment/overallResult'
import { OVERALL_RESULT_VALUES } from '@/constants/accessor/api/emergencyInspectionResult/emergencyAssessment/overallResult'
import { useInspectionResultStore } from '@/stores/inspectionResultUpsertStore'
import { S3_DIRECTORY_NAMES } from '@/constants/accessor/s3/directoryName'
import { useDisasterStore } from '@/stores/disasterStore'
import { defineRule, useField, useForm } from 'vee-validate'
import { z, ZodError } from 'zod'

export interface InspectionFieldData {
  index?: number
  title: string
  inputSelectOption: InputSelectOption[]
}

export default defineComponent({
  name: 'InspectionResult',
  components: {
    LabeledCard
  },
  props: {
    inspectionFieldData: {
      type: Object as PropType<InspectionFieldData>,
      required: true,
      default: () => ({ inputSelectOption: [], title: '' })
    },
    targetActionKey: {
      type: String,
      required: true
    },
    itemName: {
      type: String,
      required: true
    },
    triggerValidate: {
      type: Boolean,
      default: false
    },
    saveToStoreFlag: {
      type: Boolean,
      default: false
    }
  },
  emit: ['onValidationResult', 'savedInspectionResultStore'],
  setup(props, { emit }) {
    const inspectionResultStore = useInspectionResultStore()
    const disasterStore = useDisasterStore()

    /** 画像リスト */
    const imageList = ref<File[]>([])

    /** 総合判定画像のS3ディレクトリパス */
    const imageDirectory = ref<string>(
      [
        disasterStore.govCode,
        disasterStore.disasterId,
        inspectionResultStore.commonResultSummary.assessmentId,
        S3_DIRECTORY_NAMES.OVERALL_RESULT
      ].join('/') + '/'
    )

    /** 総合判定のファイル名リスト */
    const imagePathList = ref<string[]>([])

    /** 判定結果 */
    const overallResult = ref<OverallResult | null>(null)

    /** 判定結果コメントのバリデーションメッセージ */
    const commentValidationMessage = ref<string | null | undefined>(null)

    /** 総合判定ラベル */
    const overallResultLabel = computed(() => {
      const key = overallResult.value ? overallResult.value : OVERALL_RESULT_VALUES.RANK_A
      return OVERALL_RESULT_LABEL[key]
    })

    // Zod スキーマ定義
    const schema = z.string().max(300, 'コメントは300文字以内で入力してください')

    defineRule('commentValidation', (value: string) => {
      try {
        schema.parse(value)
        return true
      } catch (e) {
        if (e instanceof ZodError) {
          return e.errors[0].message
        }
        return false
      }
    })
    const { validate } = useForm()

    /** 判定結果コメント */
    const commentField = useField<string>('comment', 'commentValidation')

    watch(
      () => props.triggerValidate,
      async newValue => {
        if (newValue) {
          const isValid = await validate()
          commentValidationMessage.value = commentField.errorMessage.value
          emit('onValidationResult', isValid.valid)
        }
      }
    )

    watch(
      () => props.saveToStoreFlag,
      async newValue => {
        if (newValue) {
          // 各入力値をstoreに保存
          const param = {
            overallResult: overallResult.value,
            imageDirectory: imageDirectory.value,
            imageFilePathList: imagePathList.value,
            overallResultComment: commentField.value.value
          }
          inspectionResultStore.setEmergencyAssessment(param)
          emit('savedInspectionResultStore')
        }
      }
    )

    /**
     * 画像パスリストを更新
     * @param payload imagePathList: 画像パスリスト, key: 対象の調査項目識別用キー
     */
    const updateImagePathList = (payload: { imagePathList: string[]; key: string }): void => {
      imagePathList.value = [...payload.imagePathList]
    }

    /**
     * リアクティブ変数を初期化
     * @param なし
     */
    const initRefs = async (): Promise<void> => {
      // ディレクトリパスは初回から確定しているため、ここでは初期化せずref変数の宣言時に初期化しておく
      overallResult.value = inspectionResultStore.emergencyAssessment.overallResult
      imagePathList.value = inspectionResultStore.emergencyAssessment.imageFilePathList
      commentField.value.value = inspectionResultStore.emergencyAssessment.overallResultComment

      // 画像はindexedDBから取得
      const tmpItems = await PhotoDBOperator.getItems(imageDirectory.value, imagePathList.value)

      // Fileオブジェクトに変換した上でrefに格納
      imageList.value = [
        ...tmpItems
          .map(e => {
            if (e === undefined || e.arrayBuffer === undefined) return

            const blob = new Blob([e.arrayBuffer], { type: e.mime })
            const file = new File([blob], e.fileName, { type: e.mime })
            return file
          })
          .filter((file): file is File => file !== undefined)
      ]
    }

    onMounted(() => {
      // Storeから各入力値を取得し、ref変数に反映
      initRefs()
    })

    return {
      IMAGE_COUNT_LIMIT_FOR_INSPECTION_RESULT,
      overallResultLabel,
      S3_DIRECTORY_NAMES,
      imageList,
      imagePathList,
      commentField,
      commentValidationMessage,
      updateImagePathList
    }
  }
})
</script>

<template>
  <div class="container mt-5">
    <!-- タイトル部分 -->
    <div class="d-flex align-items-center mb-4">
      <h1 class="fw-bold flex-grow">判定結果 <span class="text-danger">【必須】</span></h1>
    </div>
    <!-- 調査項目の入力フィールド -->
    <LabeledCard
      :targetActionKey="targetActionKey"
      :itemName="itemName"
      :imageCount="imageList.length"
      :imageCountLimit="IMAGE_COUNT_LIMIT_FOR_INSPECTION_RESULT"
      :inspectionFieldData="inspectionFieldData"
      :s3DirectoryName="S3_DIRECTORY_NAMES.OVERALL_RESULT"
      :imageList="imageList"
      :imagePathList="imagePathList"
      :hideSelectBox="true"
      @updateImagePathList="updateImagePathList"
    >
      <template #overall-result>
        <div v-if="overallResultLabel">
          <div
            :class="[
              overallResultLabel.color,
              'text-black border border-black px-4 py-2 rounded shadow fs-4'
            ]"
            disabled
          >
            {{ overallResultLabel.label }}
          </div>
        </div>
      </template>
      <!-- コメント欄を footer スロットに挿入 -->
      <template #footer>
        <textarea
          v-model="commentField.value.value"
          class="form-control w-100 p-3 mb-4 fs-4"
          rows="4"
          placeholder="コメントを入力"
        ></textarea>
        <div v-if="commentValidationMessage" class="text-danger">
          {{ commentValidationMessage }}
        </div>
      </template>
    </LabeledCard>
  </div>
</template>
